import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { UserRoleService, UserRole } from '../services/user-role/user-role.service';
import { Observable, firstValueFrom, map, of, take, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  constructor(
    private userRoleService: UserRoleService,
    private router: Router
  ) {}

  // TODO(capabilities): replace role check with capability flag from /me/capabilities
  //   (e.g. capabilities.canViewAuditTrail). Keep role check until UI-wide capability
  //   service is introduced.
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {
    // Get required roles from route data
    const requiredRoles = route.data['roles'] as UserRole[];
    
    if (!requiredRoles || requiredRoles.length === 0) {
      return true; // No role restrictions
    }
    
    // Use firstValueFrom to convert Observable to Promise
    return firstValueFrom(this.userRoleService.userRole$.pipe(
      take(1),
      switchMap(currentRole => {
        // If role is null, we need to wait for it to load
        if (currentRole === null) {
          // Return a promise
          return this.waitForUserRoleObservable(requiredRoles);
        }
        
        // Check if user has required role
        if (!requiredRoles.includes(currentRole)) {
          // Redirect to unauthorized page
          this.router.navigate(['/unauthorized']);
          return of(false);
        }
        
        return of(true);
      })
    ));
  }

  private waitForUserRoleObservable(requiredRoles: UserRole[]): Observable<boolean> {
    return new Observable<boolean>(observer => {
      this.userRoleService.loadCurrentUser()
        .then(() => {
          // Get the current role after loading
          const currentRole = this.userRoleService.getCurrentRole();
          
          // Check if user has required role
          if (!currentRole || !requiredRoles.includes(currentRole)) {
            // Redirect to unauthorized page
            this.router.navigate(['/unauthorized']);
            observer.next(false);
          } else {
            observer.next(true);
          }
          observer.complete();
        })
        .catch(error => {
          console.error('Error loading user role:', error);
          this.router.navigate(['/unauthorized']);
          observer.next(false);
          observer.complete();
        });
    });
  }
}
